import { useState, useEffect, useCallback, useRef } from 'react';
import { getEntityByKey, getEntityByListAction } from '../constants';
import { fetchEntityList, addEntity, editEntityField, deleteEntity } from '../api/manageItems';

/**
 * Custom hook for managing entity lists, loading state, errors, and mutations.
 */
export const useEntityList = (entityKeyOrConfig, selectedDate = new Date(), userEmail = '') => {
  const entity = typeof entityKeyOrConfig === 'string'
    ? (getEntityByKey(entityKeyOrConfig) || getEntityByListAction(entityKeyOrConfig))
    : entityKeyOrConfig;

  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reloadTrigger, setReloadTrigger] = useState(0);

  const reload = useCallback(() => {
    setReloadTrigger(prev => prev + 1);
  }, []);

  const activeRequestRef = useRef(0);

  useEffect(() => {
    if (!entity) {
      setItems([]);
      setIsLoading(false);
      setError('Invalid entity configuration');
      return;
    }

    const requestId = ++activeRequestRef.current;
    setIsLoading(true);
    setError(null);

    fetchEntityList({ entity, date: selectedDate, userEmail })
      .then(data => {
        if (requestId === activeRequestRef.current) {
          setItems(Array.isArray(data) ? data : []);
          setIsLoading(false);
        }
      })
      .catch(err => {
        if (requestId === activeRequestRef.current) {
          setError(err.message || 'Failed to fetch items');
          setItems([]);
          setIsLoading(false);
        }
      });

    return () => {
      // Invalidate active request on cleanup
      activeRequestRef.current = requestId + 1;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entity?.listAction, selectedDate, userEmail, reloadTrigger]);

  const addItem = useCallback(async (itemData) => {
    if (!entity) throw new Error('No active entity');
    const response = await addEntity({ entity, itemData });
    setItems(prev => [...prev, itemData]);
    return response;
  }, [entity]);

  const updateItem = useCallback(async (conditionValue, field, value) => {
    if (!entity) throw new Error('No active entity');
    const response = await editEntityField({
      entity,
      conditionValue,
      field,
      value,
      date: selectedDate
    });
    setItems(prev => prev.map(item => {
      const matchKey = entity.conditionKey || 'id';
      if (item[matchKey] === conditionValue || (item.email && item.email === conditionValue)) {
        return { ...item, [field]: value };
      }
      return item;
    }));
    return response;
  }, [entity, selectedDate]);

  const removeItem = useCallback(async (id) => {
    if (!entity) throw new Error('No active entity');
    const response = await deleteEntity({ entity, id, userEmail });
    setItems(prev => prev.filter(item => item.id !== id && item[entity.conditionKey] !== id));
    return response;
  }, [entity, userEmail]);

  return {
    items,
    isLoading,
    error,
    reload,
    addItem,
    updateItem,
    removeItem,
    setItems
  };
};

export default useEntityList;
